/* eslint-disable camelcase */
import * as d3 from "d3";
import { RawData } from "./series_graph";
import { SeriesExerciseGraph } from "./series_exercise_graph";
import { i18n } from "i18n/i18n";

interface ViolinData {
    ex_id: string;
    counts: number[];
    freq: d3.Bin<number, number>[];
    average: number;
}

export class ViolinGraph extends SeriesExerciseGraph {
    protected readonly baseUrl = "/stats/violin?series_id=";

    // Scales
    private x: d3.ScaleLinear<number, number>;

    // Tooltip elements
    private tooltipIndex = -1; // Prevent unnecessary tooltip updates.
    private tooltipLine: d3.Selection<SVGLineElement, unknown, HTMLElement, unknown>;
    private tooltipLabel: d3.Selection<SVGTextElement, unknown, HTMLElement, unknown>;
    private tooltipLabels: d3.Selection<SVGTextElement, ViolinData, SVGGElement, unknown>;

    // Data
    private data: ViolinData[];
    private maxFreq = 0; // Largest frequency count (y-value).
    private readonly maxSubmissions = 20;

    /**
     * Draws the graph's SVG elements on the screen.
     *
     * @param {boolean} animation - Whether to play animations (disabled on resize redraw).
     */
    protected override draw(animation: boolean = true): void {
        super.draw(animation);

        const yBin = d3
            .scaleLinear()
            .domain([0, this.maxFreq])
            .range([0, this.y.bandwidth()]);

        const colorScale = d3
            .scaleOrdinal()
            .range(this.observable10)
            .domain(this.exerciseOrder);

        this.x = d3
            .scaleLinear()
            .domain([0, this.maxSubmissions])
            .range([5, this.innerWidth]);

        // Append bottom axis for submission counts.
        this.graph
            .append("g")
            .attr("transform", `translate(0, ${this.innerHeight})`)
            .call(
                d3
                    .axisBottom(this.x)
                    .tickFormat((t: number) =>
                        t === this.maxSubmissions ? `${t}+` : `${t}`
                    )
            )
            .select(".domain")
            .remove();

        // Append x-axis label.
        this.graph
            .append("text")
            .attr("text-anchor", "middle")
            .attr("x", this.innerWidth / 2)
            .attr("y", this.innerHeight + this.heightPerExercise / 2)
            .text(i18n.t("js.n_submissions"))
            .attr("class", "violin-label")
            .attr("fill", "currentColor");

        // Draw violin paths.
        this.graph
            .selectAll(".violin-path")
            .data(this.data)
            .join("g")
            .attr("transform", (d: ViolinData) => `translate(0, ${this.y(d.ex_id) + this.y.bandwidth() / 2})`)
            .attr("pointer-events", "none")
            .append("path")
            .attr("fill", (d: ViolinData) => colorScale(d.ex_id) as string)
            .datum((d: ViolinData) => d.freq)
            .attr("class", "violin-path")
            .attr("d", d3
                .area<d3.Bin<number, number>>()
                .x((_, i) => this.x(i))
                .y0(0)
                .y1(0)
                .curve(d3.curveMonotoneX)
            )
            .transition()
            .duration(animation ? 500 : 0)
            .attr("d", d3
                .area<d3.Bin<number, number>>()
                .x((_, i) => this.x(i))
                .y0((d: d3.Bin<number, number>) => -yBin(d.length))
                .y1((d: d3.Bin<number, number>) => yBin(d.length))
                .curve(d3.curveMonotoneX)
            );

        // Draw average submission markers.
        const dots = this.graph
            .selectAll(".avgIcon")
            .data(this.data)
            .join("circle")
            .attr("class", "avgIcon")
            .attr("cy", (d: ViolinData) => this.y(d.ex_id) + this.y.bandwidth() / 2)
            .attr("cx", (d: ViolinData) => this.x(Math.min(20, d.average)))
            .attr("r", 4)
            .attr("fill", (d: ViolinData) => d.average <= 20 ? "currentColor" : "transparent")
            .attr("stroke", "currentColor")
            .attr("stroke-width", 2)
            .style("opacity", 0);

        dots.transition().duration(animation ? 500 : 0).style("opacity", 1);

        // Add title tooltip for average markers.
        dots.append("title").text(`${i18n.t("js.mean")} ${i18n.t("js.attempts")}`);

        // Add average labels to the right side of the graph.
        const averageLabel = this.graph
            .append("g")
            .attr("transform", `translate(${this.innerWidth + 15}, 0)`);
        this.data.forEach((exercise: ViolinData) => {
            averageLabel
                .append("text")
                .attr("x", (this.margin.right - 20) / 2)
                .attr("y", this.y(exercise.ex_id) + this.y.bandwidth() / 2)
                .text(d3.format(".1f")(exercise.average))
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("fill", "currentColor")
                .style("font-size", "18px");
        });
        averageLabel
            .append("text")
            .attr("x", (this.margin.right - 20) / 2)
            .attr("y", 10)
            .text(`${i18n.t("js.mean")} ${i18n.t("js.attempts")}`)
            .attr("text-anchor", "middle")
            .attr("fill", "currentColor")
            .style("font-size", `${this.fontSize}px`);

        // Initialize tooltip elements.
        this.tooltipLine = this.graph
            .append("line")
            .attr("y1", 0)
            .attr("y2", this.innerHeight)
            .attr("opacity", 0)
            .attr("stroke", "currentColor")
            .attr("pointer-events", "none");

        this.tooltipLabel = this.graph
            .append("text")
            .attr("y", 12)
            .attr("opacity", 0)
            .attr("text-anchor", "start")
            .attr("fill", "currentColor")
            .attr("font-size", `${this.fontSize}px`)
            .attr("font-weight", "bold")
            .attr("class", "d3-tooltip-label");

        this.tooltipLabels = this.graph
            .selectAll<SVGTextElement, unknown>(".sublabels")
            .data(this.data, (d: ViolinData) => d.ex_id)
            .join("text")
            .attr("text-anchor", "end")
            .attr("fill", "currentColor")
            .attr("transform", (d: ViolinData) => `translate(0, ${this.y(d.ex_id) + this.y.bandwidth() / 2})`)
            .attr("y", 4)
            .attr("opacity", 0)
            .attr("font-size", `${this.fontSize}px`)
            .attr("class", "d3-tooltip-label sublabels");

        // Attach event listeners for tooltip interactions.
        this.svg
            .on("mousemove", (e: unknown) => this.svgMouseMove(e, this.graph))
            .on("mouseout", () => this.svgMouseOut());
    }

    /**
     * Transforms raw server data into a format usable by the violin graph.
     *
     * @param {RawData} raw - The raw data containing exercise submissions and details.
     */
    protected override processData({ data, exercises }: RawData): void {
        // Parse exercise details using provided exercise IDs.
        this.parseExercises(exercises, data.map(ex => ex.ex_id));

        // Convert raw data to structured ViolinData records.
        this.data = data.map(({ ex_id, ex_data }) => ({
            ex_id: String(ex_id),
            // Sort counts for accurate median calculation.
            counts: (ex_data as number[]).sort((a: number, b: number) => a - b),
            freq: [],
            average: 0,
        }));

        // Bin data and calculate average for each exercise.
        this.data.forEach((exercise: ViolinData) => {
            // Bin submissions using thresholds from 0 to maxSubmissions.
            exercise.freq = d3
                .bin()
                .thresholds(d3.range(0, this.maxSubmissions + 1))
                .domain([0, 1000])(exercise.counts); // Explicitly set domain to force empty bins.

            // Update the maximum frequency across all bins.
            this.maxFreq = Math.max(
                this.maxFreq,
                d3.max(exercise.freq, (bin: d3.Bin<number, number>) => bin.length) || 0
            );

            // Calculate average submissions.
            exercise.average = d3.mean(exercise.counts) || 0;
        });
    }

    /**
     * Handles mouse movement over the SVG, updating tooltip elements accordingly.
     *
     * @param {unknown} e - The mouse event.
     * @param {d3.Selection<SVGGElement, unknown, HTMLElement, unknown>} graph - The graph selection group.
     */
    private svgMouseMove(e: unknown, graph: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>): void {
        const padding = 7;
        const pointer = d3.pointer(e, graph.node());
        const xPosition = this.x.invert(pointer[0]);
        const index = Math.round(xPosition);
        const xi = this.x(index);

        if (index === this.tooltipIndex || index < 0 || xi > this.innerWidth) return;

        this.tooltipIndex = index;

        // Update vertical tooltip line position.
        this.tooltipLine
            .attr("opacity", 1)
            .transition()
            .duration(100)
            .attr("x1", xi)
            .attr("x2", xi);

        // Construct tooltip label message.
        const labelMsg = index === this.maxSubmissions ?
            `${this.maxSubmissions}+ ${i18n.t("js.submissions")}` :
            `${index} ${i18n.t(index === 1 ? "js.submission" : "js.submissions")}`;

        // Determine label alignment.
        const alignLeft = xi + (this.fontSize / 2) * labelMsg.length + 5 > this.innerWidth;
        const textAnchor = alignLeft ? "end" : "start";
        const xLabelPosition = alignLeft ? xi - padding : xi + padding;

        // Update tooltip label.
        this.tooltipLabel
            .attr("opacity", 1)
            .text(labelMsg)
            .attr("text-anchor", textAnchor)
            .transition()
            .duration(100)
            .attr("x", xLabelPosition);

        // Update sublabels for each exercise.
        this.tooltipLabels
            .attr("opacity", 1)
            .text((d: ViolinData) => {
                const freq = d.freq[Math.max(0, index)]?.length || 0;
                return `${freq} ${i18n.t(freq === 1 ? "js.user" : "js.users")}`;
            })
            .attr("text-anchor", textAnchor)
            .transition()
            .duration(100)
            .attr("x", xLabelPosition);
    }

    /**
     * Resets the tooltip index and hides tooltip elements when the mouse leaves the SVG.
     */
    private svgMouseOut(): void {
        this.tooltipIndex = -1;
        this.tooltipLine.attr("opacity", 0);
        this.tooltipLabel.attr("opacity", 0);
        this.tooltipLabels.attr("opacity", 0);
    }
}
